import {
	type ContractSuretyAccountUnderwritingModelSize,
	computeAnnualizationFactor,
	type FinancialPeriodId,
	FinancialPeriodPreparationMethod,
	formatToDollar,
	type ISODate,
	type SuretyAccountId,
} from "@inrev/common";
import { DateTime } from "luxon";
import { type FC, useMemo, useState } from "react";
import type { NonEmptyArray } from "../../shared/types";
import { Icon, SegmentedControls } from "../primitives";
import type { ColumnDef, DataGridColumnEntry, DataGridRow } from "../ui/DataGrid";
import { DataGrid } from "../ui/DataGrid";
import { PortaledTooltip } from "../ui/Tooltip";
import type { PeriodSelectorTableAction } from "./FinancialsPeriodSelectorDrawer";
import { useFinancialsPeriodSelector } from "./FinancialsPeriodSelectorProvider";
import { PeriodChangeBadge } from "./PeriodChangeBadge";
import { ppChangeTableAction, usePeriodSelectorOverlay } from "./periodSelectorOverlay";
import type { FinancialPeriodDto } from "./utils";
import type { AnalysisSourceKey, AnalysisSourceLookup } from "./utils/analysis/sources";
import {
	ANALYSIS_CATEGORIES,
	type AnalysisMetric,
	type AnalysisMetricResult,
	type AnalysisPeriodData,
	SNAPSHOT_METRIC_LABELS,
} from "./utils/analysisMetrics";
import { incomeStatementTypeLabel } from "./utils/financialStatementSlots";
import { sortFinancialPeriods } from "./utils/sortFinancialPeriods";

type AnalysisGridProps = {
	accountId: SuretyAccountId;
	financialPeriods: FinancialPeriodDto[];
	/** Pluggable data sources the metrics read from (balance sheet, income statement, WIP, debt, …). */
	sources: AnalysisSourceLookup[];
	/** The account's underwriting model size. Metrics are marked with a "*" only when the account's
	 *  size uses them; absent (or "small") marks nothing. */
	modelSize?: ContractSuretyAccountUnderwritingModelSize | null;
};

// Sub-views of the analysis grid:
//   - all:         every metric, grouped under its category header.
//   - drivers:     only the metrics that feed the account's U/W model score (the "*" metrics);
//                  hidden entirely for small/no-model accounts, which use no financial ratios.
//   - snapshot:    a short, flat quick-glance list of headline metrics (no category headers).
type AnalysisTab = "all" | "drivers" | "snapshot";
type AnalysisTabButton = { id: AnalysisTab; label: string };

const ALL_TAB: AnalysisTabButton = { id: "all", label: "All" };
const DRIVERS_TAB: AnalysisTabButton = { id: "drivers", label: "Drivers" };
const SNAPSHOT_TAB: AnalysisTabButton = { id: "snapshot", label: "Snapshot" };

const TABS_WITH_MODEL_RATIOS: NonEmptyArray<AnalysisTabButton> = [
	ALL_TAB,
	DRIVERS_TAB,
	SNAPSHOT_TAB,
];
const TABS_WITHOUT_MODEL_RATIOS: NonEmptyArray<AnalysisTabButton> = [ALL_TAB, SNAPSHOT_TAB];

type AnalysisValueKind = "dollars" | "ratio" | "percent" | "days";

/** A single computed metric value for one period. `displayValue` is rendered verbatim ("1.53x",
 *  "42.0%", "--"); when absent the numeric `value` is currency-formatted. */
type AnalysisCell = {
	value: number | null;
	displayValue?: string;
	valueKind?: AnalysisValueKind;
	/** When true, a period-over-period decrease is favorable for this metric. */
	lowerIsBetter?: boolean;
};

type AnalysisColumnPeriod = {
	id: FinancialPeriodId;
	label: string;
	periodTypeLabel: string;
	periodEndDate: ISODate;
};

/** Payload carried on each metric row; category header rows carry none and span all value columns. */
type AnalysisRowData = { cells: Map<FinancialPeriodId, AnalysisCell> };

// Matches the financial statement grids so scrollIntoView clears the sticky label column (caps at
// 360px).
const ANALYSIS_GRID_SCROLL_PADDING = { left: 360, right: 128 };

// Legacy financials mono typography with tabular figures so digits align column-to-column.
const VALUE_TEXT_CLASS = "text-sm tabular-nums";

// Single fixed width for every value column so periods render identically and stay bounded.
const PERIOD_COLUMN_WIDTH = "184px";

// One shared tooltip instance renders every metric row's formula; each info icon points at it by id.
const FORMULA_TOOLTIP_ID = "analysis-formula-tooltip";

// Flex label row so the formula info icon can sit beside the metric name.
const METRIC_LABEL_CLASS = "ml-2 flex items-center gap-1 min-w-0 flex-1 text-sm text-primary";

const periodTypeLabel = (period: FinancialPeriodDto): string =>
	`${incomeStatementTypeLabel(period.periodType)} | ${FinancialPeriodPreparationMethod.displayName(period.preparationMethod)}`;

const formatGivenValue = (value: number | null): string =>
	value === null
		? "--"
		: (formatToDollar(value, { accounting: true, round: true, omitSymbol: true }) ?? "");

const resultToCell = (result: AnalysisMetricResult, lowerIsBetter: boolean): AnalysisCell => {
	// A null result is missing data: render "--" with no numeric value so the P/P badge skips it.
	if (result === null) {
		return { value: null, displayValue: "--" };
	}
	if (result.kind === "dollars") {
		return { value: result.value, lowerIsBetter };
	}
	if (result.kind === "ratio") {
		return {
			value: result.value,
			displayValue: `${result.value.toFixed(2)}x`,
			valueKind: "ratio",
			lowerIsBetter,
		};
	}
	if (result.kind === "days") {
		return {
			value: result.value,
			displayValue: `${result.value.toFixed(1)}d`,
			valueKind: "days",
			lowerIsBetter,
		};
	}
	return {
		value: result.value,
		displayValue: `${result.value.toFixed(1)}%`,
		valueKind: "percent",
		lowerIsBetter,
	};
};

const AnalysisPeriodHeader: FC<{ period: AnalysisColumnPeriod }> = ({ period }) => (
	<div
		data-testid="period-header"
		className="flex items-center justify-center px-4 py-3 h-full relative"
	>
		<div className="flex flex-col items-center justify-center">
			<span className="text-sm font-semibold text-primary text-center">{period.label}</span>
			<span className="flex items-center justify-center text-xs text-tertiary font-semibold">
				{period.periodTypeLabel}
			</span>
		</div>
	</div>
);

/**
 * The single value column for one period. Renders the metric's pre-formatted display value (or the
 * currency-formatted number) and, when the P/P toggle is on, a favorable/unfavorable change badge
 * comparing this period against the column to its right ({@link comparePeriod}).
 */
const buildValueColumn = (
	period: AnalysisColumnPeriod,
	comparePeriod: AnalysisColumnPeriod | undefined,
	showPpChange: boolean,
): ColumnDef<AnalysisRowData> => ({
	id: `${period.id}::value`,
	label: "",
	width: PERIOD_COLUMN_WIDTH,
	navigable: true,
	visible: true,
	renderCell: (row) => {
		const cell = row.data?.cells.get(period.id) ?? null;
		const value = cell?.value ?? null;
		const compareValue =
			showPpChange && comparePeriod ? (row.data?.cells.get(comparePeriod.id)?.value ?? null) : null;

		return (
			<div className="flex items-center justify-end gap-1.5 pr-2 h-full w-full">
				<PeriodChangeBadge
					value={value}
					prevValue={compareValue}
					type={cell?.valueKind}
					colorMode={cell?.lowerIsBetter ? "lowerIsBetter" : "higherIsBetter"}
				/>
				<span className={`${VALUE_TEXT_CLASS} text-primary`}>
					{cell?.displayValue ?? formatGivenValue(value)}
				</span>
			</div>
		);
	},
});

/**
 * Analysis grid: a fixed template of metric rows grouped under non-calculated category headers. Each
 * metric is computed from the allowed values of the selected periods' active statements, read through
 * pluggable {@link AnalysisSourceLookup} sources (see {@link ANALYSIS_CATEGORIES}). A period is shown
 * only if at least one source has data for it; metrics missing a source they need render "--".
 *
 * Built directly on {@link DataGrid} (no FinancialDataGrid): the grid is read-only with a single value
 * column per period and owns its own P/P-change toggle, which it hands to the shared period selector
 * drawer as a generic table action.
 */
export const AnalysisGrid: FC<AnalysisGridProps> = ({
	accountId,
	financialPeriods,
	sources,
	modelSize,
}) => {
	const { isFinancialPeriodSelected } = useFinancialsPeriodSelector();
	const [showPpChange, setShowPpChange] = useState(false);

	const [activeTab, setActiveTab] = useState<AnalysisTab>("all");

	// Small/no-model accounts use no financial ratios, so the Model Ratios tab is dropped for them.
	const hasModelRatios = useMemo(
		() =>
			modelSize != null &&
			ANALYSIS_CATEGORIES.some((category) =>
				category.metrics.some((metric) => metric.underwritingModelSizes.includes(modelSize)),
			),
		[modelSize],
	);

	const effectiveTab: AnalysisTab = activeTab === "drivers" && !hasModelRatios ? "all" : activeTab;

	// Sort once, newest/best first, so columns render left-to-right newest→oldest and each period's
	// prior (older) period is the next column to the right.
	const chronologicalPeriods = useMemo(
		() => sortFinancialPeriods(financialPeriods),
		[financialPeriods],
	);

	const { columnPeriods, rows } = useMemo<{
		columnPeriods: AnalysisColumnPeriod[];
		rows: DataGridRow<AnalysisRowData>[];
	}>(() => {
		const selectedPeriods = chronologicalPeriods.filter((p) => isFinancialPeriodSelected(p.id));

		// Index each selected period's allowed values by source, then by row id.
		const lookupsByPeriodId = new Map<
			FinancialPeriodId,
			Map<AnalysisSourceKey, Map<string, number>>
		>();
		for (const p of selectedPeriods) {
			const byKey = new Map<AnalysisSourceKey, Map<string, number>>();
			for (const source of sources) {
				const lookup = source.lookupForPeriod(p);
				if (lookup) byKey.set(source.key, lookup);
			}
			lookupsByPeriodId.set(p.id, byKey);
		}

		// Only periods backed by at least one available source become columns.
		const eligiblePeriods = selectedPeriods.filter(
			(p) => (lookupsByPeriodId.get(p.id)?.size ?? 0) > 0,
		);

		const columnPeriods: AnalysisColumnPeriod[] = eligiblePeriods.map((p) => ({
			id: p.id,
			label: DateTime.fromISO(p.periodEndDate).toFormat("MM/dd/yyyy"),
			periodTypeLabel: periodTypeLabel(p),
			periodEndDate: p.periodEndDate,
		}));

		const dataByPeriodId = new Map<FinancialPeriodId, AnalysisPeriodData>();
		for (const p of eligiblePeriods) {
			const byKey =
				lookupsByPeriodId.get(p.id) ?? new Map<AnalysisSourceKey, Map<string, number>>();
			dataByPeriodId.set(p.id, {
				blocSize: p.blocSize ?? null,
				annualizationFactor: computeAnnualizationFactor(p),
				has: (source) => byKey.has(source),
				value: (source, rowId) => byKey.get(source)?.get(rowId) ?? null,
			});
		}

		// Annualized Revenue Growth compares against the chronologically previous (older) selected period, which
		// is the next column to the right now that periods are sorted newest-first.
		const priorByPeriodId = new Map<FinancialPeriodId, AnalysisPeriodData | null>();
		eligiblePeriods.forEach((p, index) => {
			const olderPeriod = eligiblePeriods[index + 1];
			priorByPeriodId.set(p.id, olderPeriod ? (dataByPeriodId.get(olderPeriod.id) ?? null) : null);
		});

		// No eligible periods → no rows, so the grid shows its empty message.
		if (columnPeriods.length === 0) {
			return { columnPeriods, rows: [] };
		}

		const periodCount = columnPeriods.length;

		const buildMetricRow = (
			category: (typeof ANALYSIS_CATEGORIES)[number],
			metric: AnalysisMetric,
			metricIndex: number,
		): DataGridRow<AnalysisRowData> => {
			const cells = new Map<FinancialPeriodId, AnalysisCell>();
			for (const period of columnPeriods) {
				const data = dataByPeriodId.get(period.id);
				cells.set(
					period.id,
					data
						? resultToCell(
								metric.calculate(data, priorByPeriodId.get(period.id) ?? null),
								metric.lowerIsBetter,
							)
						: { value: null, displayValue: "--" },
				);
			}
			const usedForModel = modelSize != null && metric.underwritingModelSizes.includes(modelSize);
			const label = usedForModel ? `${metric.label} *` : metric.label;
			// Row ids keep the metric's original index within its category so they stay stable across tabs.
			return {
				id: `metric::${category.key}::${metricIndex}`,
				label,
				navigable: true,
				// Metrics with a formula get an info icon beside the label; hovering it surfaces the
				// formula. Metrics that are a single line item verbatim carry no formula and keep the
				// plain label.
				renderLabel: metric.formula
					? () => (
							<span className={METRIC_LABEL_CLASS}>
								<span className="truncate">{label}</span>
								<span
									data-testid="metric-formula-info"
									data-tooltip-id={FORMULA_TOOLTIP_ID}
									data-tooltip-content={metric.formula}
									className="shrink-0 inline-flex items-center text-tertiary hover:text-secondary cursor-help"
								>
									<Icon type="formula" size="mini" aria-label={`${metric.label} formula`} />
								</span>
							</span>
						)
					: undefined,
				data: { cells },
			};
		};

		// Snapshot: a flat, explicitly-ordered curated list with no category headers. Each label is
		// resolved back to its category template so cells and the stable row id share one definition.
		if (effectiveTab === "snapshot") {
			const metricByLabel = new Map<
				string,
				{
					category: (typeof ANALYSIS_CATEGORIES)[number];
					metric: AnalysisMetric;
					metricIndex: number;
				}
			>();
			for (const category of ANALYSIS_CATEGORIES) {
				category.metrics.forEach((metric, metricIndex) => {
					metricByLabel.set(metric.label, { category, metric, metricIndex });
				});
			}
			const rows = SNAPSHOT_METRIC_LABELS.flatMap((label) => {
				const entry = metricByLabel.get(label);
				return entry ? [buildMetricRow(entry.category, entry.metric, entry.metricIndex)] : [];
			});
			return { columnPeriods, rows };
		}

		// All / Drivers: metrics grouped under their category headers; Drivers keeps only the
		// metrics that feed the account's U/W score.
		const matchesTab = (metric: AnalysisMetric): boolean =>
			effectiveTab === "drivers"
				? modelSize != null && metric.underwritingModelSizes.includes(modelSize)
				: true;

		const rows: DataGridRow<AnalysisRowData>[] = ANALYSIS_CATEGORIES.flatMap((category) => {
			const metricRows = category.metrics
				.map((metric, metricIndex) => ({ metric, metricIndex }))
				.filter(({ metric }) => matchesTab(metric))
				.map(({ metric, metricIndex }) => buildMetricRow(category, metric, metricIndex));

			if (metricRows.length === 0) return [];
			return [
				{
					id: `category::${category.key}`,
					label: category.label,
					format: { tone: "tertiary", label: { labelStyle: "caption" } },
					navigable: false,
					hideExpansionSpacer: true,
					renderCells: () => <div style={{ gridColumn: `span ${periodCount}` }} />,
				},
				...metricRows,
			];
		});

		return { columnPeriods, rows };
	}, [chronologicalPeriods, isFinancialPeriodSelected, sources, modelSize, effectiveTab]);

	const columns = useMemo<DataGridColumnEntry<AnalysisRowData>[]>(
		() =>
			columnPeriods.map((period, index) => ({
				id: period.id,
				label: period.label,
				subLabel: period.periodEndDate,
				renderGroupHeader: () => <AnalysisPeriodHeader period={period} />,
				columns: [buildValueColumn(period, columnPeriods[index + 1], showPpChange)],
			})),
		[columnPeriods, showPpChange],
	);

	const tableActions = useMemo<PeriodSelectorTableAction[]>(
		() => [ppChangeTableAction(showPpChange, () => setShowPpChange((prev) => !prev))],
		[showPpChange],
	);

	// Kept separate from headerActions so switching tabs re-renders the track without rebuilding every
	// segment element.
	const tabSegments = useMemo(
		() =>
			(hasModelRatios ? TABS_WITH_MODEL_RATIOS : TABS_WITHOUT_MODEL_RATIOS).map((tab) => (
				<SegmentedControls.Segment<AnalysisTab> key={tab.id} value={tab.id}>
					{tab.label}
				</SegmentedControls.Segment>
			)),
		[hasModelRatios],
	);

	const headerActions = useMemo(
		() => (
			<SegmentedControls<AnalysisTab>
				value={effectiveTab}
				onChange={setActiveTab}
				data-testid="analysis-tab-bar"
			>
				{tabSegments}
			</SegmentedControls>
		),
		[effectiveTab, tabSegments],
	);

	const { periodSelectorHeader, periodSelectorOverlay } = usePeriodSelectorOverlay({
		accountId,
		tableActions,
		hideExpandControls: true,
		headerActions,
		showFullLabel: false,
	});

	return (
		<>
			<DataGrid<AnalysisRowData>
				columns={columns}
				rows={rows}
				labelColumn={{ label: "Analysis", renderHeader: periodSelectorHeader }}
				overlay={periodSelectorOverlay}
				testId="analysis-grid"
				emptyMessage="No analysis data available for the selected periods."
				scrollPadding={ANALYSIS_GRID_SCROLL_PADDING}
				rowHeight={26}
				hideGroupSubHeader
			/>
			<PortaledTooltip id={FORMULA_TOOLTIP_ID} delayShow={200} place="top" />
		</>
	);
};
